import type { InstanceConfig } from '../instance/config.ts';
import { parseUrl, queryParam, resolveDisplayUrl, withQuery } from '../instance/urls.ts';
import { err, ok, type Result } from '../result.ts';
import { formatScopesParam, type AuthScope } from './scopes.ts';

/**
 * §8's `/authorize_token` grant. The user approves scopes in the instance's own UI and is
 * redirected back with a bearer token, so Visious never handles the password.
 *
 * Everything here is pure string work. The redirect itself is a **top-level navigation**, not a
 * `fetch` — which is why it needs no CORS at all, and why the URL is built against `instanceUrl`
 * (what the browser visits) rather than `transportBaseUrl` (where XHR goes).
 */
export interface AuthorizeRequest {
  readonly scopes: readonly AuthScope[];
  /** Where the instance sends the user back. Must be an absolute URL the app actually serves. */
  readonly callbackUrl: string;
  /**
   * ⚠️ An **absolute Unix epoch in seconds**, not a lifetime.
   *
   * The upstream documentation calls this parameter `expire=<seconds>`, which reads as a duration
   * — and passing one is silently accepted. The instance stores the value verbatim as the token's
   * `expire` claim and compares it against the wall clock, so `expire=2592000` ("30 days") mints a
   * token that expired in **January 1970**. Every subsequent call then fails
   * `403 {"error":"Token is expired, please try again"}`, which reads as "auth is broken" rather
   * than "that number meant something else". Measured on the wire.
   *
   * The field is named for what it is, and {@link authorizeExpiryEpochSec} exists so a duration
   * cannot be passed here by accident.
   */
  readonly expireEpochSec: number;
}

/** Turns a lifetime into the absolute epoch the instance actually wants. R9 asks for 30 days. */
export function authorizeExpiryEpochSec(nowEpochSec: number, lifetimeSec: number): number {
  return Math.floor(nowEpochSec) + Math.floor(lifetimeSec);
}

export const DEFAULT_TOKEN_LIFETIME_SEC = 30 * 24 * 60 * 60;

/** `null` when the instance URL or the callback is unusable — never a throw, never a half-built URL. */
export function buildAuthorizeUrl(config: InstanceConfig, request: AuthorizeRequest): string | null {
  if (request.scopes.length === 0) return null;

  const callback = parseUrl(request.callbackUrl);
  if (!callback || !callback.absolute) return null;
  if (!Number.isFinite(request.expireEpochSec) || request.expireEpochSec <= 0) return null;

  const base = resolveDisplayUrl(config, '/authorize_token');
  if (base === null) return null;

  return withQuery(base, {
    scopes: formatScopesParam(request.scopes),
    callback_url: request.callbackUrl,
    expire: Math.floor(request.expireEpochSec),
  });
}

export interface AuthGrant {
  /**
   * The token exactly as it goes into `Authorization: Bearer` — raw JSON text, not base64 (R9).
   * It is the credential; it must never reach a log, an error message or a URL.
   */
  readonly token: string;
  /** Undocumented but always sent, and it saves a call: the API has no "who am I" endpoint. */
  readonly username: string | null;
}

export type AuthCallbackProblem =
  /** The URL carries no `token` at all. Also how a *denial* arrives — see {@link parseAuthorizeCallback}. */
  | 'no-token'
  /** A `token=` that decodes to nothing. */
  | 'empty-token'
  /** Not a URL. */
  | 'bad-url';

/**
 * Reads the grant back off the callback URL.
 *
 * **The token is percent-encoded twice.** The instance encodes the token when it builds the
 * redirect target and the whole parameter is encoded again into the `Location` header, so the
 * `Location` carries `%257B%2522session%2522…`. Ordinary query parsing (`URLSearchParams`, or
 * core's own {@link queryParam}) decodes once and yields `%7B%22session%22…` — a string that still
 * looks tokenish, is happily stored, and is refused by every authenticated call. Verified on the
 * wire: all three encodings return 403, so the "wrong one" is not detectable by trying.
 *
 * **A denial looks like success.** The consent screen's "No" is a plain link to `callback_url`
 * with no parameters whatsoever — no `error`, no `denied`. So `no-token` is the *only* signal that
 * the user said no, and it must be treated as a normal outcome rather than as a fault.
 */
export function parseAuthorizeCallback(url: string): Result<AuthGrant, AuthCallbackProblem> {
  if (parseUrl(url) === null) return err('bad-url');

  const once = queryParam(url, 'token');
  if (once === null) return err('no-token');

  let token: string;
  try {
    token = decodeURIComponent(once);
  } catch {
    // A single-encoded token would land here. Keep what we have rather than losing the grant.
    token = once;
  }
  if (token.trim() === '') return err('empty-token');

  const username = queryParam(url, 'username');
  return ok({ token, username: username === null || username === '' ? null : username });
}

/**
 * R9 — the `token` parameter is stripped from the address bar immediately on callback, so it
 * cannot reach history, a bookmark, a screenshot or a `Referer`. Returns the URL to replace with.
 */
export function stripAuthCallbackParams(url: string): string {
  return withQuery(url, { token: null, username: null }) ?? url;
}
